import { FleetVehicleEditFormValues } from "services/api/fleet/vehicles/types";
import * as yup from "yup";
import { isVinValid } from "ui/components/TextField/VinTextField/validation";
import { VEHICLE_LOCATION_SCHEMA } from "ui/modules/fleet/vehicles/pages/components/VehicleLocationFields/validation";

export const IGNORE_ERROR = "IGNORE";

// export const FLEET_VEHICLE_EDIT_FORM_SCHEMA = yup.object().shape({
//   vehicleManufacturerId: yup
//     .string()
//     .required("Molimo Vas da odaberte marku vozila"),
//   vehicleModelId: yup.string().required("Molimo Vas da odaberete model vozila"),
//   vehicleTypeId: yup.string().required("Molimo Vas da odaberete tip vozila"),
// });

// Definiramo sheme po stepovima
const stepSchemas: { [key: number]: yup.ObjectSchema<any> } = {
  1: yup.object().shape({
    vehicleManufacturerId: yup
      .string()
      .required("Molimo Vas da odaberte marku vozila"),
    vehicleModelId: yup
      .string()
      .required("Molimo Vas da odaberete model vozila"),
    vehicleTypeId: yup.string().required("Molimo Vas da odaberete tip vozila"),
  }),
  2: yup.object().shape({
    vinNumber: yup
      .string()
      .nullable()
      .test("vin-valid", "Neispravan VIN broj (17 znakova, bez I/O/Q)", (value) => {
        if (!value) return true;
        return isVinValid(value);
      }),
    kmTraveled: yup
      .number()
      .required("Molimo Vas da unesete kilometražu")
      .min(0, "Kilometraža ne može biti negativna"),
  }),
  3: yup.object().shape({
    // validacija za step 3 - Dodatna oprema (opcionalno)
  }),
  4: yup.object().shape({
    tiresCount: yup
      .number()
      .min(4, "Molimo Vas da dodate minimalno 4 gume"),
  }),
  5: yup.object().shape({
    // validacija za step 5 - FireExtinguisher (opcionalno)
  }),
  6: yup.object().shape({
    ownerTypeId: yup
      .string()
      .required("Molimo Vas da odaberete tip vlasnika"),
    ownerContactId: yup
      .string()
      .when("ownerTypeId", {
        is: (val: string) => val && val !== "C",
        then: (schema) => schema.required("Molimo Vas da odaberete vlasnika"),
        otherwise: (schema) => schema.nullable(),
      }),
    purchaseDate: yup
      .date()
      .nullable()
      .when("ownerTypeId", {
        is: (val: string) => val && val !== "L",
        then: (schema) => schema.required("Molimo Vas da unesete datum kupnje"),
        otherwise: (schema) => schema.nullable(),
      }),
    leaseActivationDate: yup
      .date()
      .nullable()
      .when("ownerTypeId", {
        is: "L",
        then: (schema) => schema.required("Molimo Vas da unesete datum aktivacije leasinga"),
        otherwise: (schema) => schema.nullable(),
      }),
  }),
  7: yup.object().shape({
    netPrice: yup.number().required("Molimo Vas da unesete neto cijenu vozila"),
    specialTax: yup.number().nullable().min(0, "PPMV ne može biti negativan"),
    sellingPrice: yup.number().required("Molimo Vas da unesete prodajnu cijenu vozila"),
  }),
};

export const validateVehicle = async (
  formData: FleetVehicleEditFormValues,
  stepNumber: number
): Promise<{ isValid: boolean; errors: any }> => {

  const schema = stepSchemas[stepNumber];
  if (!schema) {
    return { isValid: true, errors: {} };
  }

  try {

    await schema.validate(formData, { abortEarly: false });

    return { isValid: true, errors: {} };
  } catch (err: any) {
    const errors: any = {};
    if (err.inner) {
      err.inner.forEach((e: any) => {
        if (e.path) {
          errors[e.path] = e.message;
        }
      });
    }
    return { isValid: false, errors };
  }
};

// Sheme po KLJUČU koraka (za dinamički wizard). Koraci bez validacije se izostave.
const stepSchemasByKey: { [key: string]: yup.ObjectSchema<any> } = {
  picker: yup.object().shape({
    vehicleManufacturerId: yup.string().required("Molimo Vas da odaberte marku vozila"),
    vehicleModelId: yup.string().required("Molimo Vas da odaberete model vozila"),
    vehicleTypeId: yup.string().required("Molimo Vas da odaberete tip vozila"),
  }),
  info: yup.object().shape({
    vinNumber: yup
      .string()
      .nullable()
      .test("vin-valid", "Neispravan VIN broj (17 znakova, bez I/O/Q)", (value) => {
        if (!value) return true;
        return isVinValid(value);
      }),
    kmTraveled: yup
      .number()
      .required("Molimo Vas da unesete kilometražu")
      .min(0, "Kilometraža ne može biti negativna"),
    status: yup
      .string()
      .nullable()
      .required("Molimo Vas da odaberete status vozila"),
  }),
  // Lokacija je neobavezna i korak se smije preskočiti — vozilo tada ostaje bez zapisa
  // o lokaciji dok se ne premjesti. Ako je korisnik počne popunjavati, nositelj mora
  // odgovarati vrsti; ta pravila dijeli s premještajem i modalom statusa.
  location: yup.object().shape({
    location: VEHICLE_LOCATION_SCHEMA.nullable(),
  }),
  // Gume su neobavezne — vozilo se može unijeti i bez njih (npr. tip vozila nema osovine
  // u katalogu, pa se preddefinirane gume ne mogu složiti). Dodaju se naknadno na vozilu.
  tires: yup.object().shape({}),
  owner: yup.object().shape({
    ownerTypeId: yup.string().required("Molimo Vas da odaberete tip vlasnika"),
    ownerContactId: yup.string().when("ownerTypeId", {
      is: (val: string) => val && val !== "C",
      then: (schema) => schema.required("Molimo Vas da odaberete vlasnika"),
      otherwise: (schema) => schema.nullable(),
    }),
    purchaseDate: yup.date().nullable().when("ownerTypeId", {
      is: (val: string) => val && val !== "L",
      then: (schema) => schema.required("Molimo Vas da unesete datum kupnje"),
      otherwise: (schema) => schema.nullable(),
    }),
    leaseActivationDate: yup.date().nullable().when("ownerTypeId", {
      is: "L",
      then: (schema) => schema.required("Molimo Vas da unesete datum aktivacije leasinga"),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
  price: yup.object().shape({
    netPrice: yup.number().required("Molimo Vas da unesete neto cijenu vozila"),
    specialTax: yup.number().nullable().min(0, "PPMV ne može biti negativan"),
    sellingPrice: yup.number().required("Molimo Vas da unesete prodajnu cijenu vozila"),
  }),
};

/** Validacija koraka po ključu — koristi dinamički wizard (VehicleEditCard). */
export const validateVehicleStep = async (
  formData: FleetVehicleEditFormValues,
  stepKey: string
): Promise<{ isValid: boolean; errors: any }> => {
  const schema = stepSchemasByKey[stepKey];
  if (!schema) return { isValid: true, errors: {} };
  try {
    await schema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (err: any) {
    const errors: any = {};
    if (err.inner) {
      err.inner.forEach((e: any) => {
        if (e.path) errors[e.path] = e.message;
      });
    }
    return { isValid: false, errors };
  }
};

// // Validate a report
// export const validateReportStep = (
//   formData: FleetVehicleEditFormValues,
//   stepNumber: number
// ): { isValid: boolean; errors: any } => {
//   const errors: any = {};

//   switch (stepNumber) {
//     case 1:
//       // if (!formData.location)
//       //   errors.location = `${t("errors.required")}: ${t(
//       //     "forms:fields.accidentPlace"
//       //   )}`;
//       break;
//     case 2:
//       // if (!formData.vehicles || formData.vehicles.length === 0) {
//       //   errors.vehicles = t("errors.atLeastOneVehicleRequired");
//       // }
//       break;
//     case 3:
//       break;
//     case 4:
//       break;
//     default:
//       break;
//   }

//   return {
//     isValid: Object.keys(errors).length === 0,
//     errors,
//   };
// };
